/**
 * PURCHASE MODAL
 * Confirms shop item purchase
 */

import { useState } from 'react';
import type { ShopItem } from '@/config/tisa-settings';
import { X, Coins, AlertCircle, CheckCircle } from 'lucide-react';

interface PurchaseModalProps {
  item: ShopItem | null;
  vaultBalance: number;
  onConfirm: (item: ShopItem) => Promise<void>;
  onClose: () => void;
}

export function PurchaseModal({ item, vaultBalance, onConfirm, onClose }: PurchaseModalProps) {
  const [isPurchasing, setIsPurchasing] = useState(false);

  if (!item) return null;

  const handleConfirm = async () => {
    setIsPurchasing(true);
    try {
      await onConfirm(item);
    } finally {
      setIsPurchasing(false);
    }
  };

  const newBalance = vaultBalance - item.price;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative animate-in zoom-in duration-200">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Item Icon */}
          <div className="text-center">
            <div className="text-8xl mb-4">{item.icon}</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{item.name}</h2>
            <p className="text-gray-600 mb-4">{item.description}</p>
          </div>

          {/* Price Info */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-700 font-semibold">Price:</span>
              <div className="flex items-center gap-2">
                <Coins className="w-5 h-5 text-yellow-600" />
                <span className="text-xl font-bold text-yellow-600">{item.price}</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">New Balance:</span>
              <span className={`font-bold ${newBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {newBalance} 🪙
              </span>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">After purchasing:</p>
                <p>Go to your teacher's desk to collect your item! It will be in "Pending" until delivered.</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isPurchasing}
              className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isPurchasing}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold hover:from-green-600 hover:to-emerald-700 transition disabled:opacity-50 shadow-lg flex items-center justify-center gap-2"
            >
              {isPurchasing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  Purchasing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Confirm Purchase
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
